export function initials(name: string | null | undefined): string {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * Alle tijden in de app zijn Nederlandse tijden, ook als de browser elders
 * staat (Willem in Thailand, 05-10: de planner toonde vensters en aankomsten
 * 5 uur later). Formatteer daarom nooit in de tijdzone van de browser.
 */
export const TIJDZONE = 'Europe/Amsterdam';

/** UTC-tijdstip → "HH:mm" in Nederlandse tijd; '' bij leeg of ongeldig. */
export function formatTijd(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '';
  const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: TIJDZONE });
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: TIJDZONE,
  });
}

export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIJDZONE,
  });
}

export function formatCurrency(amount: number | null | undefined): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount ?? 0);
}

export function formatEmailStatus(status: number): { label: string; color: string } {
  switch (status) {
    case 0: return { label: 'Concept', color: 'bg-slate-100 text-slate-600' };
    case 1:
    case 2:
    case 3: return { label: 'Ontvangen', color: 'bg-blue-100 text-blue-700' };
    case 4: return { label: 'Verzonden', color: 'bg-emerald-100 text-emerald-700' };
    case 5: return { label: 'Doorgestuurd', color: 'bg-slate-100 text-slate-600' };
    default: return { label: 'Onbekend', color: 'bg-slate-100 text-slate-500' };
  }
}

/** Nederlandse datum van vandaag als yyyy-MM-dd, los van de browser-tijdzone. */
export function todayISO(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: TIJDZONE }).format(new Date());
}

/** True als de yyyy-MM-dd datum vandaag of in de toekomst ligt. */
export function isTodayOrFuture(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  return dateStr.slice(0, 10) >= todayISO();
}

/** True als de yyyy-MM-dd datum vóór vandaag ligt. */
export function isPast(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  return dateStr.slice(0, 10) < todayISO();
}

export function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'zojuist';
  if (diffMin < 60) return `${diffMin} min geleden`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} uur geleden`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay} dag${diffDay === 1 ? '' : 'en'} geleden`;
  const diffWeek = Math.round(diffDay / 7);
  if (diffWeek < 5) return `${diffWeek} week${diffWeek === 1 ? '' : 'en'} geleden`;
  return formatDate(dateStr);
}
